import type { Permission, Role } from "@shoppy/shared";

/**
 * What an operator must hold for a verb to be drawn.
 *
 * Two shapes, because the api gates two ways and the console must mirror
 * whichever one actually applies. Most surfaces are gated on a PERMISSION; the
 * returns desk and the merchant queue are `@Roles('ADMIN')` on the controller,
 * and one of their permissions — `admin.refunds.issue` — is declared in
 * `@shoppy/shared/identity` and **granted to no role at all**. Gating on it would
 * hide the disputes register from the very people the api lets in. See the gap
 * list: either ADMIN should hold it or it should go.
 */
export type Requirement =
  | { readonly permission: Permission }
  | { readonly role: Role }
  /**
   * A controller gated on a LIST of roles, where no single permission spans
   * them. `reviews-admin.controller.ts` is `@Roles('ADMIN','FINANCE')` on the
   * affiliate registers and the identity package has no `affiliate.read` to
   * mirror — `reviews.affiliate.decide` is FINANCE's alone and is the WRITE.
   *
   * Naming one of the two roles would hide a register from people the api
   * admits, which is the defect the `admin.refunds.issue` note above describes
   * from the other direction. So the third arm exists rather than the check
   * being loosened to «any signed-in operator».
   */
  | { readonly anyRole: readonly Role[] };

export interface Holder {
  can(permission: Permission): boolean;
  hasRole(role: Role): boolean;
}

export function holds(holder: Holder, requirement: Requirement): boolean {
  if ("permission" in requirement) return holder.can(requirement.permission);
  if ("anyRole" in requirement) return requirement.anyRole.some((role) => holder.hasRole(role));
  return holder.hasRole(requirement.role);
}

/**
 * The drilldown — docs/24 §7.
 *
 * A register answers «what is on this list». The question a reader has NEXT is
 * «what is this row about», and without a drill the answer is a `mch_…` string
 * they copy into a search box on another screen.
 *
 * The failure this removes is a WIRING one rather than a missing feature: the id
 * is already on the client, already drawn in a column beside the figure, and
 * already answerable by an endpoint. A grid compiles alone and a panel compiles
 * alone, and nothing in between says they were meant to be a pair — so it is
 * asserted in `targets.test.ts` rather than hoped for in review.
 *
 * §7.2: **the engine reads a convention; a screen declares nothing.** Every wire
 * row spells its foreign keys the same way, because they are shaped by handlers
 * that spell them that way, so a grid drills by spreading the drill hook with no
 * declaration at all. A per-screen declaration is a per-screen chance to forget:
 * the last engine of this shape landed on four screens because four is how many
 * somebody remembered to wrap.
 */

export const DRILL_KINDS = [
  "merchant",
  "subOrder",
  "payoutBatch",
  "ledgerAccount",
  "product",
  "returnRequest",
  "fraudSubject",
  "supportTicket",
  "loyaltyAccount",
  "ticketedEvent",
] as const;
export type DrillKind = (typeof DRILL_KINDS)[number];

export interface DrillKindSpec {
  /**
   * §7.3: a capability AND a tenant are part of what a verb is. Roles here do not
   * imply downward — a catalog moderator holds no `ledger.read`, a finance
   * operator holds no `merchant.manage` — and a verb the operator cannot
   * exercise is never drawn.
   */
  readonly requires: Requirement;
  /**
   * The scope the reader must be inside. Every operator of this console is
   * platform-scoped, so every kind is `platform` today — recorded as a FIELD
   * rather than assumed, because the moment a category-scoped moderator or a
   * regional ops lead exists, a register that spans merchants drilled by a
   * scoped operator opens another merchant's record. That is BOLA, not a UI bug,
   * and the capability check alone does not catch it.
   */
  readonly scope: "platform";
  /** The registers that carry rows of this kind. §7.3's bar is two, and a reader that exists. */
  readonly carriers: readonly string[];
  /** The endpoint the panel reads. A kind whose reader has to be written first is a server change pretending to be a wiring one. */
  readonly reader: string;
}

export const DRILL_KIND_SPECS: Readonly<Record<DrillKind, DrillKindSpec>> = {
  merchant: {
    requires: { permission: "admin.merchants.approve" },
    scope: "platform",
    carriers: ["merchants", "payables", "payout-batch", "commission", "tax-issuers", "orders"],
    reader: "GET /v1/admin/merchants/:id",
  },
  subOrder: {
    requires: { permission: "admin.orders.read" },
    scope: "platform",
    carriers: ["orders", "dispatch-units"],
    reader: "GET /v1/merchant/orders/:subOrderId",
  },
  payoutBatch: {
    requires: { permission: "payouts.approve" },
    scope: "platform",
    carriers: ["payout-batches", "payables"],
    reader: "GET /v1/finance/payouts/:batchId",
  },
  ledgerAccount: {
    requires: { permission: "ledger.read" },
    scope: "platform",
    carriers: ["trial-balance", "tie-outs"],
    reader: "GET /v1/finance/accounts/:id/card",
  },
  product: {
    requires: { permission: "catalog.read" },
    scope: "platform",
    carriers: ["catalog-products", "catalog-review"],
    reader: "GET /v1/products/:slug",
  },
  returnRequest: {
    // One carrier today, which is below §7.3's bar of two — kept deliberately,
    // because the stronger rule wins: §7.2 says no register may name a
    // convention id and wire nothing to it, and a dispute row names a
    // `requestId` and nothing else that opens. The reader already exists. The
    // second carrier arrives with an admin returns register (see the gap list).
    //
    // Gated on the ROLE, not on `admin.refunds.issue`: that permission is
    // declared in `@shoppy/shared/identity` and granted to no role, while
    // `returns-admin.controller.ts` is `@Roles('ADMIN')`. Mirroring the
    // permission would hide the register from everyone the api admits.
    requires: { role: "ADMIN" },
    scope: "platform",
    carriers: ["disputes"],
    reader: "GET /v1/admin/returns/:id",
  },
  /**
   * The risk record behind a row: standing, signals, assessments and cases in
   * one read. `fraud.read` is on ADMIN as well as FINANCE, deliberately — an
   * ops operator answering «why is my order held» has to be able to see the
   * case and the reason, and refusing them that produces a support process that
   * runs on guesses. The WRITE (`fraud.decide`) is FINANCE's and lives on the
   * page's own dialog, not here: a panel is a read (§7.8).
   */
  fraudSubject: {
    requires: { permission: "fraud.read" },
    scope: "platform",
    carriers: ["fraud-cases", "fraud-lists"],
    reader: "GET /v1/admin/fraud/subjects/:kind/:id",
  },
  supportTicket: {
    requires: { permission: "support.read" },
    scope: "platform",
    carriers: ["support-tickets", "support-feedback"],
    reader: "GET /v1/admin/support/tickets/:id",
  },
  /**
   * A buyer's points, lots, wallet and gift cards. `loyalty.read` is on ADMIN
   * and FINANCE; the adjustment is FINANCE's and is a dialog on the loyalty
   * page, because it moves a balance-sheet liability.
   */
  loyaltyAccount: {
    requires: { permission: "loyalty.read" },
    scope: "platform",
    carriers: ["loyalty-lookup", "loyalty-gift-cards"],
    reader: "GET /v1/admin/loyalty/accounts/:id",
  },
  /**
   * One carrier today, which is below §7.3's bar of two — kept for the reason
   * `returnRequest` is: §7.2's stronger rule says no register may name a
   * convention id and wire nothing to it, and an event row names an `eventId`
   * and nothing else that opens.
   *
   * **`tickets.read`, not `admin.orders.read` (Wave 7).** The panel used to be
   * the public reader and nothing else, because the sales and scan figures were
   * `tickets.manage` behind `MerchantAccessGuard` and no staff role held that
   * permission. `tickets.read` is the supervisory read that closed the gap — a
   * platform operator looking at somebody else's event, read-only, never able to
   * publish, price or cancel — so it is what this panel now requires. Both
   * permissions are on ADMIN and FINANCE, so no operator loses the panel; what
   * changes is that the requirement names the read the panel actually performs
   * rather than an adjacent one.
   */
  ticketedEvent: {
    requires: { permission: "tickets.read" },
    scope: "platform",
    carriers: ["events"],
    reader: "GET /v1/events/:idOrSlug",
  },
};

/**
 * The convention, as data.
 *
 * Every `…Id` an admin row can carry is here, classified. A field mapped to
 * `null` carries its REASON for opening nothing — because "we forgot" and "there
 * is deliberately nothing to open" look identical in a table with holes in it,
 * and `targets.test.ts` refuses a field that is neither.
 */
export const CONVENTION_IDS: Readonly<Record<string, DrillKind | null>> = {
  merchantId: "merchant",
  ownerMerchantId: "merchant",
  subOrderId: "subOrder",
  batchId: "payoutBatch",
  payoutBatchId: "payoutBatch",
  accountId: "ledgerAccount",
  ledgerAccountId: "ledgerAccount",
  productId: "product",
  requestId: "returnRequest",
  returnRequestId: "returnRequest",
  /** Paired with `subjectKind`; the reader takes both, so half of it opens nothing. */
  subjectId: "fraudSubject",
  ticketId: "supportTicket",
  /**
   * NOT `accountId`, which this table already spells for a LEDGER account.
   *
   * Two contexts use the same English word for different things — a
   * double-entry account in finance, an identity account in loyalty — and a
   * convention keyed on the field name cannot tell them apart. The loyalty
   * registers therefore shape their rows with the longer name rather than the
   * engine growing a per-screen exception, which is exactly what §7.2 says a
   * per-screen declaration would become.
   */
  loyaltyAccountId: "loyaltyAccount",
  eventId: "ticketedEvent",

  // ── recorded as opening nothing, each with its reason ─────────────────────
  /** No `GET /v1/admin/orders/:id`; the api's only order reader is buyer-scoped. */
  orderId: null,
  /** An offer is read through its product; there is no offer endpoint. */
  offerId: null,
  /** Likewise a variant. */
  variantId: null,
  /** The taxonomy page IS the category reader; a panel would be a second copy of it (§7.9). */
  categoryId: null,
  brandId: null,
  /** No identity read for another account: `/v1/me` is the caller's own. */
  ownerAccountId: null,
  approvedBy: null,
  decidedBy: null,
  requestedBy: null,
  /** An issuer is a row of the tax register, which is one screen with everything on it. */
  issuerId: null,
  /** Fulfilment's node and driver readers are W4 deliverables; see the gap list. */
  nodeId: null,
  driverId: null,
  unitId: null,
  /** A dispute is read through its return request, which is the row that opens. */
  disputeId: null,

  // ── Wave 5's ids ──────────────────────────────────────────────────────────
  /** The moderation row IS the review, in full. A panel would be a second copy of it (§7.9). */
  reviewId: null,
  /** The case row carries everything `GET …/cases/:id` would; what a reader wants next is the SUBJECT. */
  caseId: null,
  assessmentId: null,
  signalId: null,
  /** There is no by-id read for a partner or a commission; both registers are one screen with everything on it. */
  partnerId: null,
  commissionId: null,
  conversionId: null,
  /** A session is read through its event, which is the row that opens. */
  sessionId: null,
  venueId: null,
  ticketTypeId: null,
  /** No identity read for another account: `/v1/me` is the caller's own. */
  requesterAccountId: null,
  assigneeAccountId: null,
  /**
   * Deliberately NOT `accountId`, for the reason `loyaltyAccountId` is not
   * either: that spelling is a LEDGER account here, and an affiliate partner's
   * account is an identity. The affiliate register therefore shapes its rows
   * with the longer name, and it opens nothing for the same reason the two
   * above do.
   */
  partnerAccountId: null,
  /** A point lot, a movement and a gift card are all drawn inside the loyalty panel already. */
  lotId: null,
  movementId: null,
  giftCardId: null,
  /** Deliberately NOT `batchId`, which this table spells for a PAYOUT batch. There is no gift-card batch reader. */
  giftCardBatchId: null,
  holdId: null,
  /** An SLA policy is a row of the policies register, which is one screen with everything on it. */
  policyId: null,
  slaPolicyId: null,
  /** An article is opened by slug from its own register; the id is what the writes take. */
  articleId: null,
  feedbackId: null,
  topicId: null,
};

export interface DrillTarget {
  readonly kind: DrillKind;
  readonly id: string;
  /**
   * Extra parameters the reader needs beyond the id. `subOrder`'s reader wants
   * `?merchantId=` for an ADMIN session, so a row carrying a `subOrderId` and no
   * `merchantId` opens NOTHING rather than opening a 403 — §7.2's «a row that
   * names nothing opens nothing, and draws no cursor».
   */
  readonly params: Readonly<Record<string, string>>;
}

const EXTRA_PARAMS: Partial<Record<DrillKind, readonly string[]>> = {
  // `orders-merchant.controller.ts` resolves the merchant from the session for a
  // MERCHANT and demands `?merchantId=` from an ADMIN. This console is only ever
  // the second case.
  subOrder: ["merchantId"],
  // The risk reader is `/subjects/:kind/:id` — the kind is half the address, not
  // a hint. A row carrying an id and no kind opens nothing, which is right: two
  // subjects of different kinds may legitimately share a value (a phone number
  // and an account id never will, but a device hash and a POD digest are both
  // 64 hex characters and the wrong one is a different record entirely).
  fraudSubject: ["subjectKind"],
};

const SLUG_FIELD: Partial<Record<DrillKind, string>> = {
  /**
   * The public product reader is by slug, not by id.
   *
   * The catalogue registers shape their rows with both, so they drill. The
   * MODERATION register cannot: `ReviewDto` carries `targetId` and no slug, and
   * there is no bulk id-to-slug read on this console's route table. So a review
   * row naming a product opens nothing and draws no hand cursor — which is the
   * behaviour `idAt` already has, recorded here so the next reader does not
   * take it for a bug.
   */
  product: "slug",
};

type Row = Record<string, unknown>;

/**
 * Every drill this row can open, in the order the fields are declared above.
 *
 * Most rows name TWO things — a sub-order names a merchant AND a buyer, a
 * shipment names a driver AND an order — and one gesture cannot offer both. The
 * first target is what double-click opens («what the row is MOST about»); the
 * rest sit at the top of the right-click menu (§7.1).
 */
export function drillsFor(row: Row | null | undefined): DrillTarget[] {
  if (!row) return [];
  const out: DrillTarget[] = [];
  const seen = new Set<string>();

  for (const [field, kind] of Object.entries(CONVENTION_IDS)) {
    if (kind === null) continue;
    const id = idAt(row, kind, field);
    if (id === null) continue;

    const key = `${kind}:${id}`;
    if (seen.has(key)) continue;

    const params: Record<string, string> = {};
    let complete = true;
    for (const name of EXTRA_PARAMS[kind] ?? []) {
      const value = row[name];
      if (typeof value !== "string" || value === "") {
        // A row carrying half of what the reader needs is a promise the
        // double-click cannot keep.
        complete = false;
        break;
      }
      params[name] = value;
    }
    if (!complete) continue;

    seen.add(key);
    out.push({ kind, id, params });
  }
  return out;
}

function idAt(row: Row, kind: DrillKind, field: string): string | null {
  const value = row[field];
  if (typeof value !== "string" || value === "") return null;

  const preferred = SLUG_FIELD[kind];
  if (preferred === undefined) return value;

  // The reader takes a slug and the id field is only what says the drill
  // applies, so a row carrying the id and not the slug opens NOTHING — half of
  // what the reader needs is a promise the double-click cannot keep (§7.2).
  const slug = row[preferred];
  return typeof slug === "string" && slug !== "" ? slug : null;
}

/**
 * What double-click opens, filtered by what this operator may actually read.
 * `null` means the row draws no hand cursor — put that in `rowClassRules`, PER
 * ROW, never on the grid.
 */
export function primaryDrill(row: Row | null | undefined, holder: Holder): DrillTarget | null {
  return drillsFor(row).find((target) => holds(holder, DRILL_KIND_SPECS[target.kind].requires)) ?? null;
}

export function allowedDrills(row: Row | null | undefined, holder: Holder): DrillTarget[] {
  return drillsFor(row).filter((target) => holds(holder, DRILL_KIND_SPECS[target.kind].requires));
}
